import { useEffect, useRef, useState } from 'react';
import { useXpGain } from './useXpGain';
import { useUserStats } from './useUserStats';
import { useCategories } from './useCategories';

// Sequence timings (ms) — mirror the mobile HeaderReward feel.
const FILL_DELAY = 320;  // wait after a gain before animating the fill
const FILL_DUR = 850;    // fill duration
const HOLD = 1100;       // hold before the transient surfaces fade out

export interface XpRewardState {
  /** Celebration in progress — drive opacity/visibility off this. */
  active: boolean;
  token: number;
  amount: number;
  domainId: string | null;
  domainColor: string | null;
  /** Animated user-level progress (0–100). */
  userPct: number;
  userLevel: number;
  /** Animated domain-level progress (0–100). */
  domPct: number;
  userLeveledUp: boolean;
  domainLeveledUp: boolean;
}

const IDLE: XpRewardState = {
  active: false,
  token: 0,
  amount: 0,
  domainId: null,
  domainColor: null,
  userPct: 0,
  userLevel: 0,
  domPct: 0,
  userLeveledUp: false,
  domainLeveledUp: false,
};

/**
 * Shared XP-reward sequencer for the desktop surfaces (rail / header / toast).
 * Wraps {@link useXpGain} and runs one transient celebration per real gain:
 * consumers fade in/out off `active`, while this hook animates the user- and
 * domain-level fills (rAF, with a setTimeout safety final for throttled tabs).
 *
 * Platform-agnostic and side-effect free, so each surface can call it
 * independently — they all observe the same underlying totalPoints gain.
 */
export function useXpReward(): XpRewardState {
  const gain = useXpGain();
  const { stats } = useUserStats();
  const { getCategoryColor } = useCategories();

  const [state, setState] = useState<XpRewardState>(IDLE);

  const timers = useRef<number[]>([]);
  const rafs = useRef<number[]>([]);
  const clearAll = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
    rafs.current.forEach((r) => cancelAnimationFrame(r));
    rafs.current = [];
  };
  useEffect(() => () => clearAll(), []);

  const animate = (from: number, to: number, dur: number, set: (v: number) => void) => {
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      set(from + (to - from) * eased);
      if (t < 1) rafs.current.push(requestAnimationFrame(step));
    };
    rafs.current.push(requestAnimationFrame(step));
  };

  useEffect(() => {
    if (!gain) return;
    clearAll();

    const domTo = gain.domainLeveledUp ? 100 : gain.domainTo;
    const userTo = gain.userLeveledUp ? 100 : gain.userTo;
    const color = gain.domainId ? getCategoryColor(gain.domainId) : null;

    setState({
      active: true,
      token: gain.token,
      amount: gain.amount,
      domainId: gain.domainId,
      domainColor: color,
      userPct: gain.userFrom,
      userLevel: stats.level,
      domPct: gain.domainFrom,
      userLeveledUp: gain.userLeveledUp,
      domainLeveledUp: gain.domainLeveledUp,
    });

    const setUserPct = (v: number) => setState((s) => ({ ...s, userPct: v }));
    const setDomPct = (v: number) => setState((s) => ({ ...s, domPct: v }));

    timers.current.push(
      window.setTimeout(() => {
        animate(gain.userFrom, userTo, FILL_DUR, setUserPct);
        animate(gain.domainFrom, domTo, FILL_DUR, setDomPct);
      }, FILL_DELAY)
    );

    // Safety final (covers throttled rAF in hidden tabs).
    timers.current.push(
      window.setTimeout(() => {
        setState((s) => ({ ...s, userPct: userTo, domPct: domTo }));
      }, FILL_DELAY + FILL_DUR)
    );

    // Exit — the whole celebration is transient on desktop.
    timers.current.push(
      window.setTimeout(() => {
        setState((s) => ({ ...s, active: false }));
      }, FILL_DELAY + FILL_DUR + HOLD)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gain?.token]);

  return state;
}
